import { FieldErrors } from 'common/components/core/FieldErrors'
import React from 'react'
import { PublicPageFieldSlugEnum } from 'src/public-pages/enums/public-page-field-slug-enum'
import { useResetPasswordSubmit } from 'src/public-pages/hooks/use-reset-password-submit'
import { WebsitePublicLogo } from 'src/public-pages/shared/components/layout/website-public-logo'
import { PublicPageButton } from 'src/public-pages/shared/components/public-page-button'
import { PublicPageField } from 'src/public-pages/shared/components/public-page-field'
import { PublicPageHeading } from 'src/public-pages/shared/components/public-page-heading'
import { PublicPageText } from 'src/public-pages/shared/components/public-page-text'
import { useTypedTranslation } from 'src/shared/i18next/hooks/use-typed-translation'
import {
  WebsiteButtonsInterface,
  WebsiteFormFieldsSettings,
  WebsiteThemeInterface,
  WebsiteTypographySettingsStylesInterface,
} from 'src/shared/types/website-settings-types'

export interface ResetYourPasswordContentProps {
  token: string
  logoUrl?: string
  isCustomDomain?: boolean
  heading?: WebsiteTypographySettingsStylesInterface
  paragraph?: WebsiteTypographySettingsStylesInterface
  theme?: WebsiteThemeInterface
  formFields?: Partial<WebsiteFormFieldsSettings>
  button?: Partial<WebsiteButtonsInterface>
}

export const ResetYourPasswordContent: React.FC<
  ResetYourPasswordContentProps
> = ({
  token,
  logoUrl,
  isCustomDomain,
  heading,
  theme,
  formFields,
  paragraph,
  button,
}) => {
  const { t } = useTypedTranslation()
  const {
    password,
    setPassword,
    confirmPassword,
    setConfirmPassword,
    handleSubmit,
    loading,
    errors,
  } = useResetPasswordSubmit(token)

  return (
    <div>
      <WebsitePublicLogo
        logoUrl={logoUrl}
        isCustomDomain={isCustomDomain}
        alt="Logo"
      />
      <PublicPageHeading
        heading={heading}
        theme={theme}
        style={{ marginBottom: '40px' }}
      >
        {t('website_public_page.reset_password.heading')}
      </PublicPageHeading>

      <div>
        <div style={{ marginBottom: '16px' }}>
          <PublicPageText
            paragraph={paragraph}
            theme={theme}
            style={{ marginBottom: '10px' }}
            requiredField
          >
            {t('website_public_page.reset_password.label')}
          </PublicPageText>

          <PublicPageField
            type={PublicPageFieldSlugEnum.Password}
            name={PublicPageFieldSlugEnum.Password}
            formFields={formFields}
            theme={theme}
            value={password}
            onChange={e => setPassword(e.target.value)}
          />
          <FieldErrors
            errors={errors?.[PublicPageFieldSlugEnum.Password]}
            fontFamily={paragraph?.fontFamily}
          />
        </div>

        <div style={{ marginBottom: '36px' }}>
          <PublicPageText
            paragraph={paragraph}
            theme={theme}
            style={{ marginBottom: '10px' }}
            requiredField
          >
            {t('website_public_page.reset_password.confirm_password.label')}
          </PublicPageText>

          <PublicPageField
            type={PublicPageFieldSlugEnum.Password}
            name={PublicPageFieldSlugEnum.ConfirmPassword}
            formFields={formFields}
            theme={theme}
            value={confirmPassword}
            onChange={e => setConfirmPassword(e.target.value)}
          />
          <FieldErrors
            errors={errors?.[PublicPageFieldSlugEnum.ConfirmPassword]}
            fontFamily={paragraph?.fontFamily}
          />
          <FieldErrors
            errors={errors?.root}
            fontFamily={paragraph?.fontFamily}
          />
        </div>

        <PublicPageButton
          button={button}
          theme={theme}
          text={t('website_public_page.reset_password.submit_button_text')}
          loading={loading}
          onClick={handleSubmit}
          width="100%"
          paragraph={paragraph}
        />
      </div>
    </div>
  )
}
